import { useAccount, useMsal } from "@azure/msal-react";
import { Flex, Modal, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useRef } from "react";
import { useDisclaimer } from "src/contexts/DisclaimerContext";
import { useArtifactHandler } from "../../utils/artifactHandler";
import { copyTableToClipboard } from "../../utils/copyToClipboard";
import { CopyToClipboardButton } from "../CopyToClipboardButton";
import { MessageItemCode } from "../MessageItemCode";
import { AsyncPdfViewer } from "../TopToolbarCustomActions";

interface MDComponentProps {
    node?: unknown;
    children?: React.ReactNode;
    href?: string;
    className?: string;
}

interface MDCodeProps {
    node?: unknown;
    children?: React.ReactNode;
    className?: string;
    inline?: boolean;
}

interface MDAWithPreviewProps {
    node?: unknown;
    href?: string;
    children?: React.ReactNode;
    src?: string;
    [key: string]: unknown;
}

export const MDTable = ({ node, className, ...props }: MDComponentProps) => {
    const tableRef = useRef<HTMLTableElement>(null);

    const handleCopyTable = async () => {
        if (!tableRef.current) return;
        await copyTableToClipboard(tableRef.current);
    };

    return (
        <div className="markdownTableWrapper">
            <CopyToClipboardButton
                onCopy={handleCopyTable}
                tooltip="Copy table"
                size="sm"
                className="tableCopyButton"
            />
            <Table
                ref={tableRef}
                className={className ? `markdownTable ${className}` : "markdownTable"}
                highlightOnHover
                fz="sm"
                verticalSpacing="xs"
                horizontalSpacing="sm"
                withTableBorder={false}
                withColumnBorders={false}
                {...props}
            />
        </div>
    );
};

export const MDCode = ({ node, ...props }: MDCodeProps) => <MessageItemCode {...props} />;

export const MDA = ({ node, children, ...props }: MDComponentProps) => {
    const { instance } = useMsal();
    const account = useAccount();
    const apiKey = account?.idToken;
    const openPDFModal = false;
    const [opened, { open, close }] = useDisclosure(false);
    const { getFullUrl, handleArtifactClick, isArtifactURL } = useArtifactHandler();
    const { openDisclaimer } = useDisclaimer();

    // Filter out javascript: URLs to prevent React security warnings
    const safeHref = props.href?.startsWith("javascript:") ? "#" : props.href;

    if (isArtifactURL(props.href)) {
        const fullUrl = getFullUrl(props.href || "");
        return (
            <>
                <Modal opened={opened} onClose={close} withCloseButton={false} size="90vw">
                    <MDAWithPdfPreview node={node} instance={instance} apiKey={apiKey} {...props}>
                        {children}
                    </MDAWithPdfPreview>
                </Modal>
                <a
                    target="_blank"
                    href={fullUrl}
                    onClick={(e) => {
                        if (openPDFModal) {
                            e.preventDefault();
                            open();
                        } else {
                            // Let handleArtifactClick decide whether to prevent default
                            handleArtifactClick(e, props.href || "", [], openDisclaimer);
                        }
                    }}
                    rel="noreferrer"
                >
                    {children}
                </a>
            </>
        );
    }
    return (
        <a target="_blank" {...props} href={safeHref}>
            {children}
        </a>
    );
};

const MDAWithPdfPreview = ({ node, ...props }: MDAWithPreviewProps) => {
    const { instance } = useMsal();
    const account = useAccount();
    const apiKey = account?.idToken;
    const { getFullUrl } = useArtifactHandler();

    if (props.href?.endsWith(".pdf")) {
        const fullUrl = getFullUrl(props.href);
        return (
            <Flex gap="md" align="center">
                <AsyncPdfViewer
                    pdfUrl={fullUrl}
                    apiKey={apiKey}
                    row={fullUrl.split("object_name=")[1] || ""}
                    instance={instance}
                />
            </Flex>
        );
    }
    if (props.href?.endsWith(".html")) {
        const fullUrl = getFullUrl(props.href);
        return (
            <a target="_blank" href={fullUrl} rel="noreferrer">
                {props.children}
            </a>
        );
    }
    return (
        <a target="_blank" href={props.href} {...props}>
            {props.children}
        </a>
    );
};
